"use client";

import React, { useState } from "react";
const sectors = [{
  id: "psyche",
  number: "I",
  title: "Psyche & Mentale Klarheit",
  focus: "Die innere Ebene",
  description: "Neue Sichtweisen gewinnen, hilfreiche gedankliche Gewohnheiten aufbauen, zur Selbstregulation und Selbstreflexion befähigen, z. B. durch systemisch-lösungsorientierte Ansätze, Embodiment, Achtsamkeit und Meditation, die Entwicklung metakognitiver Fähigkeiten.",
  color: "#673DBD",
  bgHover: "hover:bg-[#673DBD]/5",
  borderHover: "hover:border-[#673DBD]",
  borderRadius: "md:rounded-tl-3xl md:rounded-tr-3xl md:rounded-bl-3xl md:rounded-br-[4rem]"
}, {
  id: "structure",
  number: "II",
  title: "Struktur & Organisation",
  focus: "Die äußere Ebene",
  description: "Günstige Strukturen und Abläufe im Alltag oder im Team schaffen, z. B. durch die Arbeit an grundlegenden Haltungen, Visionen, Leitbildern, Veränderung von Prozessen, Kommunikation, Führung, dem Umgang mit Fehlern oder Unterschiedlichkeit oder der Art, wie Entscheidungen getroffen werden.",
  color: "#0E6D7D",
  bgHover: "hover:bg-[#0E6D7D]/5",
  borderHover: "hover:border-[#0E6D7D]",
  borderRadius: "md:rounded-tl-3xl md:rounded-tr-3xl md:rounded-br-3xl md:rounded-bl-[4rem]"
}, {
  id: "body",
  number: "III",
  title: "Körper & Bewegung",
  focus: "Die physische Ebene",
  description: "Die untrennbare Einheit von Geist und Körper nutzen, um das Nervensystem über Bewegung zu regulieren, z. B. durch Embodimentübungen, Tai Chi und Qi Gong, Yoga oder der gezielten Arbeit an Körper- und Spannungsmustern.",
  color: "#2A5D37",
  bgHover: "hover:bg-[#2A5D37]/5",
  borderHover: "hover:border-[#2A5D37]",
  borderRadius: "md:rounded-tl-3xl md:rounded-bl-3xl md:rounded-br-3xl md:rounded-tr-[4rem]"
}, {
  id: "nature",
  number: "IV",
  title: "Natur & Umgebung",
  focus: "Die regenerative Ebene",
  description: "Durch den direkten Rückzug in unser ursprüngliches Zuhause Reize herunterfahren und tiefe Erdung erfahren, z. B. bei Walk & Talk (Beratung im Gehen), Wahrnehmungsübungen in der Natur, die Arbeit mit Naturmetaphern und Archetypen.\xA0",
  color: "#845C3E",
  bgHover: "hover:bg-[#845C3E]/5",
  borderHover: "hover:border-[#845C3E]",
  borderRadius: "md:rounded-tr-3xl md:rounded-bl-3xl md:rounded-br-3xl md:rounded-tl-[4rem]"
}];
export default function ResilienceOrganicSystem() {
  const [hoveredSector, setHoveredSector] = useState(null);
  return <section id="organic-resilience-system" className="bg-background text-foreground w-full py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(#2A5D3704_1.5px,transparent_1.5px)] bg-[size:32px_32px] pointer-events-none z-0"></div>
      
      <div className="container mx-auto px-4 relative z-10 max-w-5xl">
        <div className="text-center mb-16 md:mb-24 max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-6 leading-tight">
            Was bedeutet Resilienz?
          </h2>
          <p className="font-sans text-muted-foreground text-base md:text-lg leading-relaxed mb-6">Resilienz ist die Fähigkeit, Belastungen standzuhalten, in Krisen wieder aufzustehen und an Herausforderungen zu wachsen. Sie ist keine starre Eigenschaft, sondern ein dynamischer Prozess, der entwickelt werden kann.&nbsp;</p>
          <p className="font-sans text-muted-foreground text-base md:text-lg leading-relaxed mb-6"><strong data-ast-id="f:/src/pages/Home/sections/Fourpillarflowgraphic.tsx|sig:wnmxfe" data-ast-start="2812" data-ast-end="2973">Auf individueller Ebene&nbsp;</strong>schützt Resilienz die persönliche psychische Gesundheit und körperliche Kraft vor Erschöpfung. Sie hilft uns, Stressmuster zu erkennen, Emotionen weise zu regulieren und auch in stürmischen Zeiten zu innerer Ruhe und Klarheit zu finden.</p>
          <p className="font-sans text-muted-foreground text-base md:text-lg leading-relaxed mb-16"><strong data-ast-id="f:/src/pages/Home/sections/Fourpillarflowgraphic.tsx|sig:w9b00y" data-ast-start="3332" data-ast-end="3376">Auf organisationaler Ebene&nbsp;</strong>ist sie ein tragendes Fundament, das Teams, Unternehmen oder soziale Einrichtungen (wie Kitas und Verwaltungen) flexibel und handlungsfähig hält. Eine resiliente Organisation zeichnet sich durch gesunde Strukturen aus, die Reibungsverluste minimieren und Belastungen auch bei hohem Druck oder dünner Personaldecke auffangen. Krisen werden in resilienten Organisationen als Entwicklungsanlässe und Lernchancen genutzt.</p>
          
          <span className="font-sans text-sm uppercase tracking-widest text-primary font-semibold block mb-4">
            Unser ganzheitlicher Ansatz: Vier Bereiche, ein System
          </span>
          <p className="font-sans text-muted-foreground text-base md:text-lg leading-relaxed mb-4">
            Echte und nachhaltige Widerstandskraft steht auf verschiedenen Säulen. Sind mehrere davon stark und stabil, kommt ein System ins Wanken - jedoch auch schnell wieder auf die Füße.
          </p>
          <p className="font-sans text-muted-foreground text-base md:text-lg leading-relaxed mb-6">
            Deshalb verfolgen wir einen ganzheitlichen Ansatz und ziehen gezielt Ressourcen aus vier elementaren Fachbereichen, die organisch ineinandergreifen:
          </p>
          
        </div>

        <div className="relative mt-16">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex items-center justify-center z-20 pointer-events-none">
            <div className="w-36 h-36 rounded-full bg-background border-2 flex flex-col items-center justify-center shadow-lg transition-all duration-500 scale-100" style={{
            borderColor: hoveredSector ? hoveredSector.color : "var(--border)",
            boxShadow: hoveredSector ? `0 10px 30px -10px ${hoveredSector.color}40` : "none"
          }}>
              <span className="font-serif text-lg font-bold tracking-wider transition-colors duration-300" style={{
              color: hoveredSector ? hoveredSector.color : "var(--foreground)"
            }}>
                Resilienz
              </span>
              <span className="font-sans text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                Zentrum
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 relative z-10">
            {sectors.map(sector => <div key={sector.id} onMouseEnter={() => setHoveredSector(sector)} onMouseLeave={() => setHoveredSector(null)} className={`group relative flex flex-col bg-card/40 backdrop-blur-md border border-border/60 p-8 md:p-10 transition-all duration-500 ease-out hover:shadow-xl rounded-2xl ${sector.borderRadius} ${sector.bgHover} ${sector.borderHover}`} style={{
            borderLeft: `6px solid ${sector.color}`
          }}>
                <div className="flex items-baseline justify-between mb-4">
                  <span className="font-sans text-xs uppercase tracking-wider font-semibold opacity-70" style={{
                color: sector.color
              }}>
                    {sector.focus}
                  </span>
                  <span className="font-serif text-2xl font-bold opacity-20 group-hover:opacity-60 transition-opacity duration-300" style={{
                color: sector.color
              }}>
                    {sector.number}
                  </span>
                </div>

                <h3 className="font-serif text-2xl font-bold text-foreground mb-4 transition-colors duration-300">
                  {sector.title}
                </h3>

                <p className="font-sans text-base text-muted-foreground leading-relaxed flex-1">
                  {sector.description}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}